import { ArrowRight } from "lucide-react";

import { Footer } from "@/components/Frame";
import { Field, PasswordField } from "@/components/Field";
import { validEmail, validRFC } from "@/lib/validators";

type V = {
  email: string;
  password: string;
  nombre: string;
  rfc: string;
  telefono: string;
  terms: boolean;
};

export function Identity({
  v,
  set,
  touched,
  blur,
  emailCollision,
  onContinue,
}: {
  v: V;
  set: <K extends keyof V>(k: K, val: V[K]) => void;
  touched: Partial<Record<keyof V, boolean>>;
  blur: (k: keyof V) => void;
  emailCollision: boolean;
  onContinue: () => void;
}) {
  const emailErr =
    touched.email && !validEmail(v.email)
      ? v.email
        ? "Correo no válido."
        : "Ingresa tu correo."
      : "";
  const pwErr =
    touched.password && v.password.length < 8 ? "Mínimo 8 caracteres." : "";
  const nameErr =
    touched.nombre && v.nombre.trim().length < 3
      ? "Ingresa tu nombre completo."
      : "";
  const rfcValid = v.rfc.trim() !== "" && validRFC(v.rfc);
  const rfcErr =
    touched.rfc && v.rfc.trim() !== "" && !validRFC(v.rfc) ? "RFC no válido." : "";

  const canContinue =
    validEmail(v.email) &&
    v.password.length >= 8 &&
    v.nombre.trim().length >= 3 &&
    v.terms &&
    !emailCollision &&
    (v.rfc.trim() === "" || validRFC(v.rfc));

  return (
    <>
      <div className="screen">
        <h1 className="h1">Crea tu cuenta</h1>
        <p className="lede">
          Usaremos estos datos para tu primera factura y tu acceso.
        </p>

        <div style={{ marginTop: 22 }}>
          <Field
            label="Correo de trabajo"
            required
            type="email"
            inputMode="email"
            autoComplete="email"
            value={v.email}
            onChange={(e) => set("email", e.target.value)}
            onBlur={() => blur("email")}
            error={emailErr}
            valid={!emailErr && validEmail(v.email)}
          />

          {emailCollision && (
            <div className="banner banner--info" style={{ marginTop: 8 }}>
              Ya existe una cuenta pendiente con este correo.
            </div>
          )}

          <PasswordField
            value={v.password}
            onChange={(val) => set("password", val)}
            onBlur={() => blur("password")}
            error={pwErr}
          />

          <Field
            label="Nombre y apellido"
            required
            type="text"
            autoComplete="name"
            value={v.nombre}
            onChange={(e) => set("nombre", e.target.value)}
            onBlur={() => blur("nombre")}
            error={nameErr}
            valid={v.nombre.trim().length >= 3 && !nameErr}
          />

          <Field
            label="RFC (opcional)"
            type="text"
            inputMode="text"
            autoComplete="off"
            value={v.rfc}
            onChange={(e) => set("rfc", e.target.value.toUpperCase())}
            onBlur={() => blur("rfc")}
            error={rfcErr}
            valid={rfcValid}
          />

          <Field
            label="Teléfono (opcional)"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={v.telefono}
            onChange={(e) => set("telefono", e.target.value)}
          />

          <label className="terms">
            <input
              type="checkbox"
              checked={v.terms}
              onChange={(e) => set("terms", e.target.checked)}
            />
            <span>
              He leído y acepto los{" "}
              <a href="/legal/terminos" target="_blank" rel="noreferrer">
                Términos
              </a>{" "}
              y la{" "}
              <a href="/legal/privacidad" target="_blank" rel="noreferrer">
                Política de Privacidad
              </a>
              .
            </span>
          </label>
        </div>
      </div>
      <Footer>
        <button
          className="btn btn--primary btn--block"
          disabled={!canContinue}
          onClick={onContinue}
        >
          Continuar <ArrowRight size={20} />
        </button>
      </Footer>
    </>
  );
}
